import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotateCw, Settings, ZoomIn, ZoomOut } from 'lucide-react';
import SectionBackButton from '../components/SectionBackButton';
import { normalizeWorkoutWheelConfig } from '../lib/workoutWheelConfig';
import { fetchWorkoutWheelConfig } from '../lib/workoutWheelApi';
import type { WorkoutWheelConfig, WorkoutWheelExerciseRow, WorkoutWheelTier } from '../types';

const TIER_FILL: Record<WorkoutWheelTier, string> = {
  green: '#16a34a',
  yellow: '#eab308',
  red: '#dc2626',
};

const TIER_LABEL: Record<WorkoutWheelTier, string> = {
  green: 'Easy',
  yellow: 'Medium',
  red: 'Hard',
};

const PICK_MODAL_PANEL: Record<WorkoutWheelTier, string> = {
  green:
    'border-2 border-green-600 bg-green-50 text-gray-900 dark:bg-green-950/70 dark:border-green-500 dark:text-green-50',
  yellow:
    'border-2 border-amber-500 bg-amber-50 text-gray-900 dark:bg-amber-950/70 dark:border-amber-400 dark:text-amber-50',
  red: 'border-2 border-red-600 bg-red-50 text-gray-900 dark:bg-red-950/70 dark:border-red-500 dark:text-red-50',
};

const SPIN_MS = 10000;
const SPIN_FULL_TURNS = 5;
const WHEEL_ZOOM_SCALE = 3.5;

function normDeg(d: number): number {
  return ((d % 360) + 360) % 360;
}

/** arcF0/arcF1 = fraction around the circle [0,1), CW from top; fixed after deals so removals leave gaps, no reslice jump. */
interface WheelSegment {
  uid: string;
  tier: WorkoutWheelTier;
  row: WorkoutWheelExerciseRow;
  segmentKey: string;
  arcF0: number;
  arcF1: number;
}

interface PickedResult {
  seg: WheelSegment;
  doubled: boolean;
}

interface PendingPick {
  seg: WheelSegment;
  finalRotation: number;
  doubled: boolean;
}

function segmentUid(tier: WorkoutWheelTier, rowId: string, copyIndex: number): string {
  const rnd =
    typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  return `${tier}-${rowId}-${copyIndex}-${rnd}`;
}

/** Repeating slice order: green → yellow → red → yellow → green → green. */
const WHEEL_TIER_PATTERN: ('g' | 'y' | 'r')[] = ['g', 'y', 'r', 'y', 'g', 'g'];
const WHEEL_PATTERN_LEN = WHEEL_TIER_PATTERN.length;

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Tier band order follows WHEEL_TIER_PATTERN; pass per-tier lists already shuffled for random wedge placement. */
function weaveGYRYG(g: WheelSegment[], y: WheelSegment[], r: WheelSegment[]): WheelSegment[] {
  const out: WheelSegment[] = [];
  let gi = 0;
  let yi = 0;
  let ri = 0;
  let pi = 0;
  const total = g.length + y.length + r.length;

  while (out.length < total) {
    let took = false;
    for (let k = 0; k < WHEEL_PATTERN_LEN; k++) {
      const idx = (pi + k) % WHEEL_PATTERN_LEN;
      const t = WHEEL_TIER_PATTERN[idx];
      if (t === 'g' && gi < g.length) {
        out.push(g[gi++]);
        pi = (idx + 1) % WHEEL_PATTERN_LEN;
        took = true;
        break;
      }
      if (t === 'y' && yi < y.length) {
        out.push(y[yi++]);
        pi = (idx + 1) % WHEEL_PATTERN_LEN;
        took = true;
        break;
      }
      if (t === 'r' && ri < r.length) {
        out.push(r[ri++]);
        pi = (idx + 1) % WHEEL_PATTERN_LEN;
        took = true;
        break;
      }
    }
    if (!took) {
      if (gi < g.length) out.push(g[gi++]);
      else if (yi < y.length) out.push(y[yi++]);
      else if (ri < r.length) out.push(r[ri++]);
    }
  }
  return out;
}

function buildPool(config: WorkoutWheelConfig): WheelSegment[] {
  const greens: WheelSegment[] = [];
  const yellows: WheelSegment[] = [];
  const reds: WheelSegment[] = [];
  const add = (
    bucket: WheelSegment[],
    tier: WorkoutWheelTier,
    rows: WorkoutWheelExerciseRow[],
    copies: number
  ) => {
    for (const row of rows) {
      for (let c = 0; c < copies; c++) {
        bucket.push({
          uid: segmentUid(tier, row.id, c),
          tier,
          row,
          segmentKey: `${tier}:${row.id}`,
          arcF0: 0,
          arcF1: 1,
        });
      }
    }
  };
  add(greens, 'green', config.green, config.copiesGreen);
  add(yellows, 'yellow', config.yellow, config.copiesYellow);
  add(reds, 'red', config.red, config.copiesRed);
  const woven = weaveGYRYG(
    shuffleArray(greens),
    shuffleArray(yellows),
    shuffleArray(reds)
  );
  const n = woven.length;
  if (n === 0) return woven;
  return woven.map((s, i) => ({
    ...s,
    arcF0: i / n,
    arcF1: (i + 1) / n,
  }));
}

function slicePathArc(arcF0: number, arcF1: number, radius: number): string {
  if (arcF1 <= arcF0) return '';
  const start = arcF0 * 2 * Math.PI - Math.PI / 2;
  const end = arcF1 * 2 * Math.PI - Math.PI / 2;
  const x1 = radius * Math.cos(start);
  const y1 = radius * Math.sin(start);
  const x2 = radius * Math.cos(end);
  const y2 = radius * Math.sin(end);
  const largeArc = end - start > Math.PI ? 1 : 0;
  return `M 0 0 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
}

/** Label along slice radius: anchored in the outer half, reading toward the hub; rotation kept within ±90° for legibility. */
function labelPositionArc(arcF0: number, arcF1: number, radius: number): { x: number; y: number; rot: number } {
  const midF = (arcF0 + arcF1) / 2;
  const mid = midF * 2 * Math.PI - Math.PI / 2;
  const r = radius * 0.78;
  const x = r * Math.cos(mid);
  const y = r * Math.sin(mid);
  let rotDeg = (Math.atan2(-Math.sin(mid), -Math.cos(mid)) * 180) / Math.PI;
  if (rotDeg > 90) rotDeg -= 180;
  else if (rotDeg < -90) rotDeg += 180;
  return { x, y, rot: rotDeg };
}

const WorkoutWheel: React.FC = () => {
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [config, setConfig] = useState<WorkoutWheelConfig>(() =>
    normalizeWorkoutWheelConfig(null)
  );
  const [pool, setPool] = useState<WheelSegment[]>([]);
  const [results, setResults] = useState<(PickedResult | null)[]>([]);
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [pendingPick, setPendingPick] = useState<PendingPick | null>(null);
  const [wheelZoomed, setWheelZoomed] = useState(false);
  /** After spin lands; user must confirm before slice(s) leave the wheel. */
  const [pickToConfirm, setPickToConfirm] = useState<PickedResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await fetchWorkoutWheelConfig();
        if (cancelled) return;
        const next = saved ?? normalizeWorkoutWheelConfig(null);
        setConfig(next);
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : 'Could not load wheel config');
        setConfig(normalizeWorkoutWheelConfig(null));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setPool(buildPool(config));
    setResults(Array.from({ length: config.spinCount }, () => null));
    setRotation(0);
    setPendingPick(null);
    setIsSpinning(false);
    setPickToConfirm(null);
  }, [config]);

  const spinsDone = results.filter(Boolean).length;
  const canSpin =
    !isSpinning &&
    !pendingPick &&
    !pickToConfirm &&
    pool.length > 0 &&
    spinsDone < config.spinCount;

  const resetSession = useCallback(() => {
    setPool(buildPool(config));
    setResults(Array.from({ length: config.spinCount }, () => null));
    setRotation(0);
    setPendingPick(null);
    setIsSpinning(false);
    setPickToConfirm(null);
  }, [config]);

  const spinWheel = useCallback((doubled: boolean) => {
    if (isSpinning || pendingPick) return;
    if (pool.length === 0) return;
    if (results.filter(Boolean).length >= config.spinCount) return;
    const n = pool.length;
    const pickedIndex = Math.floor(Math.random() * n);
    const seg = pool[pickedIndex];
    const centerF = (seg.arcF0 + seg.arcF1) / 2;
    const centerOnWheel = centerF * 360;
    const prevMod = normDeg(rotation);
    const align = normDeg(360 - centerOnWheel - prevMod);
    const add = SPIN_FULL_TURNS * 360 + align;
    const finalRotation = rotation + add;
    setPendingPick({ seg, finalRotation, doubled });
    setIsSpinning(true);
    setRotation(finalRotation);
  }, [config.spinCount, isSpinning, pendingPick, pool, results, rotation]);

  const onSpin = useCallback(() => {
    if (!canSpin) return;
    spinWheel(false);
  }, [canSpin, spinWheel]);

  const onWheelTransitionEnd = (e: React.TransitionEvent<SVGGElement>) => {
    if (e.propertyName !== 'transform') return;
    if (!pendingPick || !isSpinning) return;
    const { seg, doubled } = pendingPick;
    setPickToConfirm({ seg, doubled });
    setIsSpinning(false);
    setPendingPick(null);
  };

  const onConfirmPick = useCallback(() => {
    if (!pickToConfirm) return;
    const { seg, doubled } = pickToConfirm;
    setResults((prev) => {
      const i = prev.findIndex((x) => x === null);
      if (i < 0) return prev;
      const next = [...prev];
      next[i] = { seg, doubled };
      return next;
    });
    setPool((p) => p.filter((s) => s.segmentKey !== seg.segmentKey));
    setPickToConfirm(null);
  }, [pickToConfirm]);

  const onRespin = useCallback(() => {
    if (!pickToConfirm) return;
    setPickToConfirm(null);
    spinWheel(true);
  }, [pickToConfirm, spinWheel]);

  const wheelRadius = 88;
  const nSeg = pool.length;

  return (
    <>
      <SectionBackButton to="/gym" label="Back to Gym" />
      <div className="min-h-screen bg-theme-secondary pb-24 px-4 pt-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-start justify-between gap-2 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-theme-primary">Spin the Workout Wheel</h1>
              <p className="text-theme-secondary text-sm mt-1">
                {config.spinCount} spins — same exercise + tier removes all matching slices.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/gym/workout-wheel/settings')}
              className="shrink-0 p-2 rounded-lg border border-theme-primary bg-theme-primary text-theme-secondary hover:opacity-90"
              aria-label="Wheel settings"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>

          {loadError && (
            <p className="text-amber-600 dark:text-amber-400 text-sm mb-2">{loadError} — using defaults.</p>
          )}

          <div className="w-full overflow-x-auto overflow-y-visible flex justify-center mb-8 mt-4 px-1">
            <div
              className="relative flex justify-center transition-transform duration-300 ease-out"
              style={{
                transform: wheelZoomed ? `scale(${WHEEL_ZOOM_SCALE})` : 'scale(1)',
                transformOrigin: 'center top',
              }}
            >
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 z-10 w-0 h-0 
                border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-theme-primary drop-shadow"
                aria-hidden
              />
              {pickToConfirm?.doubled && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-9 z-20">
                  <span className="inline-flex items-center rounded-full bg-amber-500/95 text-amber-950 text-[16px] font-black px-3 py-1 border border-amber-300 shadow-lg">
                    x2
                  </span>
                </div>
              )}
              <div className="w-[min(100vw-2rem,320px)] aspect-square">
                  <svg viewBox="-100 -100 200 200" className="w-full h-full drop-shadow-md">
                    <g
                      style={{
                        transform: `rotate(${rotation}deg)`,
                        transformOrigin: '0px 0px',
                        transition: isSpinning
                          ? `transform ${SPIN_MS}ms cubic-bezier(0.15, 0.85, 0.15, 1)`
                          : 'none',
                      }}
                      onTransitionEnd={onWheelTransitionEnd}
                    >
                      {nSeg === 0 ? (
                        <text
                          textAnchor="middle"
                          dominantBaseline="middle"
                          className="fill-theme-secondary text-[8px]"
                        >
                          Add exercises in settings
                        </text>
                      ) : (
                        <>
                          <circle r={wheelRadius} fill="var(--workout-wheel-gap)" />
                          {pool.map((seg) => {
                            const { x, y, rot } = labelPositionArc(seg.arcF0, seg.arcF1, wheelRadius);
                            const short =
                              seg.row.name.length > 10
                                ? `${seg.row.name.slice(0, 9)}…`
                                : seg.row.name;
                            const fill = TIER_FILL[seg.tier];
                            const textFill = seg.tier === 'yellow' ? '#1f2937' : '#fafafa';
                            return (
                              <g key={seg.uid}>
                                <path
                                  d={slicePathArc(seg.arcF0, seg.arcF1, wheelRadius)}
                                  fill={fill}
                                  stroke="#0f172a"
                                  strokeOpacity={0.25}
                                />
                                <text
                                  x={x}
                                  y={y}
                                  fill={textFill}
                                  fontSize={nSeg > 40 ? 5 : nSeg > 24 ? 6 : 7}
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  transform={`rotate(${rot}, ${x}, ${y})`}
                                  style={{ pointerEvents: 'none' }}
                                >
                                  {short}
                                </text>
                              </g>
                            );
                          })}
                        </>
                      )}
                      <circle r="14" className="fill-theme-primary" stroke="currentColor" strokeWidth={1} />
                    </g>
                  </svg>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mb-8">
            <button
              type="button"
              data-testid="workout-wheel-spin"
              disabled={!canSpin}
              onClick={onSpin}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCw className={`w-4 h-4 ${isSpinning ? 'animate-spin' : ''}`} />
              {isSpinning ? 'Spinning…' : 'Spin'}
            </button>
            <button
              type="button"
              onClick={resetSession}
              disabled={isSpinning}
              className="px-4 py-2 rounded-lg border border-theme-primary text-theme-primary disabled:opacity-40"
            >
              Reset round
            </button>
            <button
              type="button"
              onClick={() => setWheelZoomed((z) => !z)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-theme-primary text-theme-primary hover:opacity-90"
              aria-pressed={wheelZoomed}
              aria-label={wheelZoomed ? 'Zoom out wheel' : 'Zoom in on wheel'}
            >
              {wheelZoomed ? (
                <>
                  <ZoomOut className="w-4 h-4 shrink-0" />
                  Zoom out
                </>
              ) : (
                <>
                  <ZoomIn className="w-4 h-4 shrink-0" />
                  Zoom in
                </>
              )}
            </button>
          </div>

          {pool.length === 0 && spinsDone < config.spinCount && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-4">
              Not enough slices left for more spins. Reset the round or add more exercises in settings.
            </p>
          )}

          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-theme-primary">Today&apos;s picks</h2>
            {Array.from({ length: config.spinCount }, (_, i) => {
              const pick = results[i];
              return (
                <div
                  key={i}
                  className="rounded-lg border border-theme-primary bg-theme-primary px-4 py-3 text-theme-primary"
                >
                  <span className="text-theme-secondary text-sm font-medium mr-2">#{i + 1}</span>
                  {pick ? (
                    <>
                      <span
                        className={`text-xs font-semibold uppercase mr-2 ${
                          pick.seg.tier === 'green'
                            ? 'text-green-600 dark:text-green-400'
                            : pick.seg.tier === 'yellow'
                              ? 'text-yellow-700 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400'
                        }`}
                      >
                        {TIER_LABEL[pick.seg.tier]}
                      </span>
                      <span className="font-medium">{pick.seg.row.name}</span>
                      <span className="text-theme-secondary"> — {pick.seg.row.prescription}</span>
                      {pick.doubled && (
                        <span className="ml-2 inline-flex items-center rounded-full bg-amber-300/80 dark:bg-amber-500/30 text-amber-900 dark:text-amber-200 text-[12px] font-bold px-2 py-0.5">
                          x2
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-theme-secondary">—</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap gap-4 text-xs text-theme-secondary">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm" style={{ background: TIER_FILL.green }} /> Easy
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm" style={{ background: TIER_FILL.yellow }} /> Medium
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm" style={{ background: TIER_FILL.red }} /> Hard
            </span>
          </div>
        </div>
      </div>

      {pickToConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="workout-wheel-pick-title"
        >
          <div
            className={`w-full max-w-sm rounded-2xl shadow-2xl p-6 ${PICK_MODAL_PANEL[pickToConfirm.seg.tier]}`}
          >
            <p
              id="workout-wheel-pick-title"
              className="text-xs font-bold uppercase tracking-wide opacity-90"
            >
              Picked — {TIER_LABEL[pickToConfirm.seg.tier]} {pickToConfirm.doubled ? '(x2)' : ''}
            </p>
            <p className="text-xl font-semibold mt-2 text-balance">{pickToConfirm.seg.row.name}</p>
            <p className="text-sm mt-1 opacity-90">{pickToConfirm.seg.row.prescription}</p>
            {pickToConfirm.doubled && (
              <div className="mt-4 inline-flex items-center rounded-full bg-amber-500/95 text-amber-950 px-3 py-1.5 border border-amber-300 shadow">
                <span className="text-[16px] font-black leading-none">x2</span>
                <span className="ml-2 text-xs font-bold uppercase tracking-wide opacity-80">
                  double
                </span>
              </div>
            )}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={onRespin}
                className="py-2.5 rounded-lg font-semibold border border-current/40 hover:opacity-90"
              >
                Re-spin
              </button>
              <button
                type="button"
                onClick={onConfirmPick}
                className="py-2.5 rounded-lg font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900 hover:opacity-90"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkoutWheel;
